/**
 * Base storage service for graph-model entities (Route, Plan, Activity).
 *
 * Extends OfflineFirstStorageService with:
 *  - Auto-creation of a root LayerGroup on entity create
 *  - has-layer-group edge wiring
 *  - Cascade deletion of all edges when an entity is removed
 */

import type { Table } from 'dexie'
import { db } from '../database/schema.js'
import type { SyncEvent } from '../database/schema.js'
import { OfflineFirstStorageService, type StorageResult } from './base-storage.js'
import { isApplyingServerChanges } from './server-apply.js'
import { edgeService, nodeRef } from '../services/edge-service.js'
import { featureStorage } from './feature-storage.js'
import type { LayerGroup } from '../schemas/layer-group.js'

export abstract class EntityStorageService<
  T extends { id?: string; createdAt?: string; updatedAt?: string; createdBy?: string },
> extends OfflineFirstStorageService<T> {
  /** The node type used in edge references (e.g. 'route', 'plan', 'activity') */
  protected abstract readonly nodeType: string

  protected constructor(table: Table<T>, entityType: string) {
    super(table, entityType)
  }

  /**
   * Create entity, auto-create root LayerGroup, and wire up the has-layer-group edge.
   */
  async createWithLayerGroup(
    data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>,
    layerGroupName?: string
  ): Promise<StorageResult<T>> {
    const result = await this.create(data)
    if (!result.success || !result.data) return result

    const entity = result.data
    const entityId = (entity as { id: string }).id
    const createdBy = (entity as { createdBy?: string }).createdBy ?? ''

    // Create root layer group for this entity
    const now = new Date().toISOString()
    const group: LayerGroup = {
      id: crypto.randomUUID(),
      name: layerGroupName ?? (data as { name?: string }).name ?? this.entityType,
      color: '#4A90D9',
      layerType: 'custom',
      isSystemGroup: true,
      displayOrder: 0,
      isVisible: true,
      isExpanded: true,
      featureIds: [],
      createdBy,
      createdAt: now,
      updatedAt: now,
    }
    await db.layerGroups.add(group)

    // Wire up entity → layer group edge
    await edgeService.create({
      subject: nodeRef(this.nodeType, entityId),
      predicate: 'has-layer-group',
      object: nodeRef('group', group.id),
    })

    return result
  }

  /**
   * Delete entity and cascade-delete all children: layer groups, nested
   * child groups, features within those groups, and all edges.
   */
  async deleteWithEdges(id: string): Promise<StorageResult<void>> {
    const ref = nodeRef(this.nodeType, id)

    // Collect root layer groups
    const rootGroupIds = await edgeService.getObjectIds(ref, 'has-layer-group')

    // Recursively collect all groups (root + nested children)
    const allGroupIds: string[] = []
    const collectGroups = async (groupId: string): Promise<void> => {
      allGroupIds.push(groupId)
      const childIds = await edgeService.getObjectIds(nodeRef('group', groupId), 'has-child')
      for (const childId of childIds) {
        await collectGroups(childId)
      }
    }
    for (const gid of rootGroupIds) {
      await collectGroups(gid)
    }

    // Delete all features within all groups
    for (const groupId of allGroupIds) {
      const featureIds = await edgeService.getObjectIds(nodeRef('group', groupId), 'has-feature')
      for (const fid of featureIds) {
        await featureStorage.deleteFeature(fid)
      }
    }

    // Delete all edges referencing this entity
    await edgeService.deleteAllForNode(ref)

    // Delete all layer groups and their edges
    for (const groupId of allGroupIds) {
      await edgeService.deleteAllForNode(nodeRef('group', groupId))
      const group = await db.layerGroups.get(groupId)
      const groupVersion = (group as unknown as Record<string, unknown> | undefined)?.version as
        | number
        | undefined
      await db.layerGroups.delete(groupId)
      if (!isApplyingServerChanges()) {
        const event: SyncEvent = {
          id: crypto.randomUUID(),
          entityType: 'layerGroup',
          entityId: groupId,
          operation: 'delete',
          data: groupVersion != null ? { version: groupVersion } : {},
          timestamp: new Date().toISOString(),
          conflictResolved: false,
        }
        db.syncEvents.add(event).catch(() => {})
      }
    }

    // Capture version before deleting so sync can send the correct expectedVersion
    const entity = await this.table.get(id)
    const version = (entity as Record<string, unknown> | undefined)?.version as number | undefined

    // Delete the entity itself
    const result = await this.deleteById(id)
    if (result.success) {
      this.createSyncEvent(id, 'delete', version != null ? { version } : undefined)
    }
    return result
  }

  /**
   * Get the root layer group ID for this entity.
   */
  async getRootLayerGroupId(entityId: string): Promise<string | undefined> {
    const ids = await edgeService.getObjectIds(nodeRef(this.nodeType, entityId), 'has-layer-group')
    return ids[0]
  }
}
